import './styles/base.css';
import { renderDailyCommand } from './views/dailyCommand';
import { renderFileThis } from './views/fileThis';
import { renderVisionMap } from './views/visionMap';
import { renderRecordDetail } from './views/recordDetail';
import { renderSettings } from './views/settings';
import { initSync, onSyncStatus, type SyncStatus } from './lib/sync';
import { getDb, seedInitialRecordsIfEmpty } from './db/indexeddb';

const STATUS_LABELS: Record<SyncStatus, string> = {
  offline: 'Offline',
  'online-unsynced': 'Unsynced',
  syncing: 'Syncing…',
  synced: 'Synced',
  error: 'Sync error',
  'no-token': 'Local only',
};

const TABS: Array<{ path: string; label: string; icon: string }> = [
  { path: '#/', label: 'Command', icon: '⌘' },
  { path: '#/file', label: 'File This', icon: '✎' },
  { path: '#/map', label: 'Map', icon: '☷' },
  { path: '#/settings', label: 'Settings', icon: '⚙' },
];

const appRoot = document.getElementById('app')!;

function shellHtml(): string {
  return `
    <div class="topbar">
      <div class="brand">
        <span class="brand-dot"></span>
        <h1>God's Vision Map</h1>
      </div>
      <span class="sync-pill" id="sync-pill" data-status="offline">…</span>
    </div>
    <div class="page" id="page"></div>
    <nav class="tabbar" id="tabbar">
      ${TABS.map((t) => `<a href="${t.path}" data-path="${t.path}"><span class="icon">${t.icon}</span><span>${t.label}</span></a>`).join('')}
    </nav>
  `;
}

function updateActiveTab(hash: string) {
  const base = hash.split('?')[0];
  const tabPath = base.startsWith('#/record') ? '#/map' : (base || '#/');
  document.querySelectorAll('#tabbar a').forEach((a) => {
    const p = (a as HTMLElement).dataset.path;
    a.classList.toggle('active', p === tabPath || (p === '#/' && tabPath === '#/'));
  });
}

async function route(): Promise<void> {
  const page = document.getElementById('page')!;
  const [rawHash, queryString] = window.location.hash.slice(1).split('?');
  const hash = rawHash || '/';
  const query = new URLSearchParams(queryString ?? '');
  updateActiveTab(window.location.hash || '#/');

  if (hash === '/' || hash === '') {
    await renderDailyCommand(page);
  } else if (hash === '/file') {
    await renderFileThis(page);
  } else if (hash === '/map') {
    await renderVisionMap(page, query);
  } else if (hash.startsWith('/record/')) {
    const id = decodeURIComponent(hash.slice('/record/'.length));
    await renderRecordDetail(page, id, (h) => { window.location.hash = h; });
  } else if (hash === '/settings') {
    await renderSettings(page);
  } else {
    page.innerHTML = '<div class="empty">Not found.</div>';
  }
  window.scrollTo(0, 0);
}

async function bootstrap(): Promise<void> {
  appRoot.innerHTML = shellHtml();
  await getDb(); // ensure IndexedDB stores exist before first render
  await seedInitialRecordsIfEmpty(); // first-run only; no-op once any record exists

  const pill = document.getElementById('sync-pill')!;
  const setPill = (status: SyncStatus, pending: number) => {
    pill.dataset.status = status;
    pill.textContent = pending > 0 && status !== 'syncing'
      ? `${STATUS_LABELS[status]} (${pending})`
      : STATUS_LABELS[status];
  };
  onSyncStatus(setPill);
  initSync();

  window.addEventListener('hashchange', () => { void route(); });
  await route();
}

void bootstrap();
